import { useCallback, useEffect, useRef } from 'react';
import { installFetchWrapper, registerResetTimerCallback, registerUnauthorizedCallback } from './apiFetchWrapper.js';

const TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Custom hook to handle user inactivity timeout for HIPAA compliance.
 * Automatically logs out users after 30 minutes of inactivity.
 *
 * Activity is detected through:
 * - API calls to /api/* endpoints (via global fetch wrapper)
 * - React Router navigation changes
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.isAuthenticated - Whether user is currently authenticated
 * @param {Function} options.onLogout - Callback function to execute on logout
 * @param {Function} [options.onUnauthorized] - Callback when /api/* returns 401
 * @param {Function} [options.refreshAuth] - Silent auth revalidation (navigation / tab focus)
 * @param {Object} options.location - Location object from react-router-dom's useLocation hook
 */
export function useInactivityTimer({ isAuthenticated, onLogout, onUnauthorized, refreshAuth, location }) {
    const timerRef = useRef(null);
    const onLogoutRef = useRef(onLogout);
    const onUnauthorizedRef = useRef(onUnauthorized);
    const refreshAuthRef = useRef(refreshAuth);

    useEffect(() => {
        onLogoutRef.current = onLogout;
    }, [onLogout]);

    useEffect(() => {
        onUnauthorizedRef.current = onUnauthorized;
    }, [onUnauthorized]);

    useEffect(() => {
        refreshAuthRef.current = refreshAuth;
    }, [refreshAuth]);

    const resetTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            console.log('Cleared existing timer');
        }

        if (isAuthenticated) {
            timerRef.current = setTimeout(() => {
                console.log('Inactivity timeout: Logging out user after 30 minutes of inactivity');
                sessionStorage.setItem('inactivityLogout', 'true');
                if (onLogoutRef.current) {
                    onLogoutRef.current();
                }
                timerRef.current = null;
            }, TIMEOUT_DURATION);
        }
    }, [isAuthenticated]);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            console.log('Cleared existing timer');
        }
    }, []);

    useEffect(() => {
        registerResetTimerCallback(resetTimer);
        registerUnauthorizedCallback(() => {
            if (onUnauthorizedRef.current) {
                onUnauthorizedRef.current();
            }
        });
        installFetchWrapper();
    }, [resetTimer]);

    useEffect(() => {
        if (isAuthenticated) {
            resetTimer();
        } else {
            clearTimer();
        }

        return () => {
            clearTimer();
        };
    }, [isAuthenticated, resetTimer, clearTimer]);

    useEffect(() => {
        if (isAuthenticated && location && refreshAuthRef.current) {
            refreshAuthRef.current();
            resetTimer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location?.pathname, isAuthenticated, resetTimer]);

    useEffect(() => {
        if (!isAuthenticated) {
            clearTimer();
        }
    }, [isAuthenticated, clearTimer]);

    useEffect(() => {
        if (!isAuthenticated) {
            return undefined;
        }

        function handleVisibilityChange() {
            if (document.visibilityState === 'visible' && refreshAuthRef.current) {
                refreshAuthRef.current();
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isAuthenticated]);
}
